import React from "react";
import styled from "styled-components";
import { C2Text, T1Text } from "../text/CustomText";
import { Theme } from "../../Theme";

// 래퍼 컴포넌트: SVG와 텍스트를 겹치기 위해 relative 포지션 설정
const Wrapper = styled.div`
    position: relative;
    display: inline-flex; // 크기를 컨텐츠에 맞춤
    align-items: center;
    justify-content: center;
    width: ${(props) => props.size}px;
    height: ${(props) => props.size}px;
`;

// SVG 컨테이너: 원의 시작점을 12시 방향으로 돌리기 위해 transform 사용
const StyledSvg = styled.svg`
    width: 100%;
    height: 100%;
    transform: rotate(-90deg);
    display: block; // SVG 하단의 불필요한 공백 제거
`;

// 모든 원의 기본 스타일 (공통)
const CircleBase = styled.circle`
    fill: none; // 채우기 없음
    stroke-width: ${(props) => props.strokeWidth}px;
`;

// 배경 트랙 (연한 보라색)
const CircleBackground = styled(CircleBase)`
    stroke: ${Theme.color.indigo500_01};
`;

// 진행 상태 바 (그라데이션)
const CircleProgress = styled(CircleBase)`
    stroke: url(#progressGradient);
    stroke-linecap: round;
    stroke-dasharray: ${(props) => props.circumference};
    stroke-dashoffset: ${(props) => props.offset};
    transition: stroke-dashoffset 0.3s ease-out;
`;

// 중앙 텍스트 컨테이너
const TextContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
`;

/**
 * 원형 프로그레스 바 컴포넌트
 * @param {number} value - 현재 값 (예: 14)
 * @param {number} max - 최대 값 (예: 25)
 * @param {string} [label="Complete"] - 상단 레이블
 * @param {number} [size=180] - 컴포넌트 크기 (px)
 * @param {number} [strokeWidth=20] - 바의 두께 (px)
 */
const CircularProgressChart = ({ value, max, label = "Complete", size = 180, strokeWidth = 20 }) => {
    // SVG 계산
    const center = size / 2; // 원의 중심
    const radius = (size - strokeWidth) / 2; // 반지름 (두께 제외)
    const circumference = 2 * Math.PI * radius; // 원의 둘레

    // 진행률 계산
    const percentage = (value / max) * 100;
    const offset = circumference - (percentage / 100) * circumference;

    return (
        <Wrapper size={size}>
            <StyledSvg viewBox={`0 0 ${size} ${size}`}>
                {/* SVG 그라데이션 정의 (defs)
                  이미지의 위쪽(밝은 보라)에서 아래쪽(어두운 보라)으로 적용
                */}
                <defs>
                    <linearGradient id="progressGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor={Theme.color.violet500} />
                        <stop offset="100%" stopColor={Theme.color.indigo800} />
                    </linearGradient>
                </defs>

                {/* 1. 배경 트랙 */}
                <CircleBackground strokeWidth={strokeWidth} cx={center} cy={center} r={radius} />

                {/* 2. 진행 상태 바 */}
                <CircleProgress
                    strokeWidth={strokeWidth}
                    circumference={circumference}
                    offset={offset}
                    cx={center}
                    cy={center}
                    r={radius}
                />
            </StyledSvg>

            {/* 3. 중앙 텍스트 */}
            <TextContainer>
                <C2Text fontColor={Theme.color.gray500}>{label}</C2Text>
                <T1Text fontColor={Theme.color.black} fontWeight={"700"}>
                    {value}/{max}
                </T1Text>
            </TextContainer>
        </Wrapper>
    );
};

export default CircularProgressChart;
